import { type ComponentProps } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button-v25/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu-next';
import AddIcon from '@atlaskit/icon/core/add';
import BoardIcon from '@atlaskit/icon/core/board';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { token } from '@atlaskit/tokens';

import { JsmPlaygroundSearchField } from '../components/jsm-playground-search-field';

import { engineeringMessages as messages } from './jsm-playground-engineering-messages';

type ButtonIcon = NonNullable<ComponentProps<typeof Button>['iconAfter']>;
type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const AddViewButtonIcon: IconButtonIcon = () => <AddIcon label="" size="small" />;
const FilterChevronIcon: ButtonIcon = () => <ChevronDownIcon label="" size="small" />;
const MoreActionsIcon: IconButtonIcon = () => <ShowMoreHorizontalIcon label="" size="small" />;

export const JsmPlaygroundEngineeringToolbar = () => {
	const intl = useIntl();

	return (
		<>
			<div css={pageHeaderStyles}>
				<h3 css={pageTitleStyles}>
					<FormattedMessage {...messages.incomingRequests} />
				</h3>
				<div css={viewBarStyles}>
					<div css={viewTabsStyles}>
						<button aria-pressed css={activeViewStyles} type="button">
							<ListBulletedIcon label="" size="small" />
							<FormattedMessage {...messages.list} />
						</button>
					</div>
					<div css={viewBarRemainderStyles}>
						<span aria-hidden css={viewBarRuleStyles} />
						<span css={addViewDropdownStyles}>
							<DropdownMenu
								placement="bottom-start"
								shouldRenderToParent
								testId="engineering-add-view"
								trigger={({ triggerRef, ...triggerProps }) => (
									<IconButton
										{...triggerProps}
										ref={triggerRef}
										appearance="subtle"
										icon={AddViewButtonIcon}
										label={intl.formatMessage(messages.addView)}
										spacing="compact"
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem
										elemBefore={<CalendarIcon color="currentColor" label="" size="small" />}
									>
										<FormattedMessage {...messages.calendar} />
									</DropdownItem>
									<DropdownItem
										elemBefore={<BoardIcon color="currentColor" label="" size="small" />}
									>
										<FormattedMessage {...messages.board} />
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</span>
					</div>
				</div>
			</div>

			<div css={toolbarStyles}>
				<div css={searchStyles}>
					<JsmPlaygroundSearchField
						label={intl.formatMessage(messages.searchWork)}
						placeholder={intl.formatMessage(messages.searchWork)}
					/>
				</div>
				{[messages.requestType, messages.status, messages.assignee].map((message) => (
					<span aria-hidden css={inertControlStyles} key={message.id}>
						<Button iconAfter={FilterChevronIcon} tabIndex={-1}>
							{intl.formatMessage(message)}
						</Button>
					</span>
				))}
				<span css={toolbarSpacerStyles} />
				<span aria-hidden css={inertControlStyles}>
					<Button tabIndex={-1}>
						<FormattedMessage {...messages.triage} />
					</Button>
				</span>
				<span aria-hidden css={inertControlStyles}>
					<IconButton
						icon={MoreActionsIcon}
						label={intl.formatMessage(messages.moreActions)}
						tabIndex={-1}
					/>
				</span>
			</div>
		</>
	);
};

const pageHeaderStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	paddingBlockStart: token('space.300'),
	paddingInline: token('space.300'),
});
const pageTitleStyles = css({
	marginBlock: 0,
	fontFamily: token('font.family.body'),
	fontSize: '20px',
	fontWeight: 600,
	lineHeight: '20px',
});
const viewBarStyles = css({ display: 'flex', alignItems: 'flex-end' });
const viewTabsStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	alignItems: 'center',
});
const activeViewStyles = css({
	display: 'inline-flex',
	height: token('space.500'),
	alignItems: 'center',
	gap: token('space.075'),
	paddingBlockStart: 0,
	paddingBlockEnd: 0,
	paddingInline: token('space.100'),
	borderBlockStartWidth: 0,
	borderInlineStartWidth: 0,
	borderInlineEndWidth: 0,
	borderBlockEndWidth: token('border.width.focused', '2px'),
	borderBlockEndStyle: 'solid',
	borderBlockEndColor: token('color.border.selected'),
	backgroundColor: 'transparent',
	color: token('color.text.selected'),
	font: token('font.body'),
	cursor: 'pointer',
	'&:hover': { backgroundColor: token('color.background.neutral.subtle.hovered') },
	'&:focus-visible': {
		outlineWidth: token('border.width.focused', '2px'),
		outlineStyle: 'solid',
		outlineColor: token('color.border.focused'),
		outlineOffset: `calc(-1 * ${token('border.width.focused', '2px')})`,
	},
});
const viewBarRemainderStyles = css({
	position: 'relative',
	display: 'flex',
	minWidth: 0,
	flex: '1 1 auto',
	alignSelf: 'stretch',
	alignItems: 'center',
	paddingInlineStart: token('space.050'),
});
const viewBarRuleStyles = css({
	position: 'absolute',
	insetBlockEnd: 0,
	insetInlineStart: 0,
	insetInlineEnd: 0,
	height: token('border.width', '1px'),
	backgroundColor: token('color.border'),
});
const addViewDropdownStyles = css({
	position: 'relative',
	display: 'inline-flex',
	alignItems: 'center',
});
const toolbarStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	paddingBlockEnd: token('space.150'),
	paddingInline: token('space.300'),
});
const searchStyles = css({ width: '220px', flexShrink: 0 });
const toolbarSpacerStyles = css({ flex: '1 1 auto' });
const inertControlStyles = css({ display: 'inline-flex', pointerEvents: 'none' });
